<ng-template #locationWebhookModalFormContent role="document" let-modal>

  <app-page-comp-header #pageHeader
    [showBottomBorder]="false"
    [title]="formTitle"
  >
  </app-page-comp-header>

  <form (ngSubmit)="locationWebhookFormSubmitAttempt()" [formGroup]="locationWebhookForm" class="tablelist-form" autocomplete="off">
    <div class="modal-body" id="locationWebhookFormDetails">

      @if (hasFieldSelection) {
        <ul ngbNav #webhookNav="ngbNav" [(activeId)]="activeTab" class="nav nav-pills arrow-navtabs nav-success bg-light mb-3">
          <li [ngbNavItem]="navItem_Webhook">
            <a ngbNavLink><span class="d-sm-block">Webhook</span></a>
          </li>
          <li [ngbNavItem]="navItem_Configuration">
            <a ngbNavLink><span class="d-sm-block">Configuration</span></a>
          </li>
        </ul>
      }

      @if (activeTab==navItem_Configuration) {
        <p class="text-muted mb-3">
          Fields to include in each message sent to the configured endpoint
        </p>

        <div class="webhook-field-grid" formGroupName="fields">
          <div class="webhook-field-required">
            <span class="webhook-field-required-title text-muted fs-12">Required</span>
            @for (field of requiredFields; track field.fieldKey) {
              <ng-container *ngTemplateOutlet="fieldRow; context: { $implicit: field }"></ng-container>
            }
          </div>

          @for (field of optionalFields; track field.fieldKey) {
            <ng-container *ngTemplateOutlet="fieldRow; context: { $implicit: field }"></ng-container>
          }

          <ng-template #fieldRow let-field>
            <div class="form-check webhook-field-row">
              <input class="form-check-input m-0" type="checkbox" [formControlName]="field.fieldKey" [id]="'webhook-field-' + field.fieldKey" />
              <label class="form-check-label text-nowrap" [for]="'webhook-field-' + field.fieldKey">{{ field.displayName }}</label>
              <label class="form-check-label text-nowrap" [for]="'webhook-field-' + field.fieldKey"><code class="webhook-field-key">{{ field.fieldKey }}</code></label>
            </div>
          </ng-template>
        </div>
      }

      @if (activeTab==navItem_Webhook) {
      <p class="text-muted mb-3">
        SureSIM will send a POST request whenever a SIM changes country, carrying the location details
      </p>

      <div class="mb-3">
        <label class="form-label" for="endpointUrl-field">Endpoint URL</label>
        <input
          autocomplete="no-fill"
          class="form-control"
          [class.is-invalid]="formSubmitted && formData['endpointUrl'].errors"
          data-lpignore="true"
          formControlName="endpointUrl"
          id="endpointUrl-field"
          placeholder="https://example.com/suresim/location-webhook"
          type="text"
        />
        @if(formSubmitted && formData['endpointUrl'].hasError('required')) {
          <div class="invalid-feedback">A value is required</div>
        } @else if(formSubmitted && formData['endpointUrl'].hasError('maxlength')) {
          <div class="invalid-feedback">Maximum length of {{endpointUrlMaxLength}} characters has been exceeded</div>
        } @else if(formSubmitted && formData['endpointUrl'].hasError('pattern')) {
          <div class="invalid-feedback">Please enter a valid https:// URL</div>
        }
      </div>

      <div class="mb-3">
        <label class="form-label" for="secret-field">Secret/token <span class="text-muted">(optional)</span></label>
        <input autocomplete="new-password" data-lpignore="true" type="password" id="secret-field" class="form-control"
               placeholder="Sent as an Authorization: Bearer header" formControlName="secret" />
        <div class="form-text">Leave unchanged to keep the stored secret. Sent to your endpoint as a bearer token.</div>
      </div>

      @if (hasPartnerIdRef) {
        <div class="mb-3">
          <label class="form-label" for="partnerIdRef-field">Partner ID Reference <span class="text-muted">(optional)</span></label>
          <input
            autocomplete="no-fill"
            class="form-control"
            [class.is-invalid]="formSubmitted && formData['partnerIdRef'].errors"
            data-lpignore="true"
            formControlName="partnerIdRef"
            id="partnerIdRef-field"
            type="text"
          />
          @if (formSubmitted && formData['partnerIdRef'].hasError('maxlength')) {
            <div class="invalid-feedback">Maximum length of {{partnerIdRefMaxLength}} characters has been exceeded</div>
          }
          <div class="form-text">Your own reference for this company. Sent as partnerIdRef when selected on the Configuration tab.</div>
        </div>
      }

      <div class="form-check form-switch mb-2">
        <input class="form-check-input" type="checkbox" role="switch" id="active-field" formControlName="active" />
        <label class="form-check-label" for="active-field">Active</label>
      </div>

      @if (lastFailure != undefined) {
        <div class="alert alert-warning py-2 mt-3 mb-0">
          <i class="ri-error-warning-line align-bottom me-1"></i>
          <strong>Live delivery is failing:</strong>
          {{ lastFailure.timestamp | date: 'dd MMM yyyy HH:mm':'UTC' }}
          @if (lastFailure.httpStatusCode != null) { &bull; HTTP {{ lastFailure.httpStatusCode }} }
          @if (lastFailure.errorMessage) { &bull; {{ lastFailure.errorMessage }} }
        </div>
      }

      @if (testInProgress) {
        <div class="d-flex align-items-center gap-2 mt-3 text-muted">
          <span class="spinner-border spinner-border-sm" role="status"></span>
          <span>{{ testWasReplay ? 'Replaying last country change...' : 'Sending test request...' }}</span>
        </div>
      }

      @if (!testInProgress && testResult != undefined) {
        <div class="border rounded p-3 mt-3">

          <div class="d-flex align-items-center flex-wrap gap-2 mb-2">
            <span class="badge webhook-status-badge" [class.bg-success]="testResult.success" [class.bg-danger]="!testResult.success">
              {{ testResult.success ? 'Delivered' : 'Failed' }}
            </span>
            <span class="text-muted">{{ testWasReplay ? 'Replay of last country change' : 'Test request' }}</span>
            @if (testResult.httpStatusCode != null) {
              <span class="text-muted">&bull; HTTP {{ testResult.httpStatusCode }}</span>
            }
            @if (testResult.elapsedMs != null) {
              <span class="text-muted">&bull; {{ testResult.elapsedMs }} ms</span>
            }
          </div>

          @if (testResult.errorMessage) {
            <div class="alert alert-danger py-2 mb-2">{{ testResult.errorMessage }}</div>
          }

          @if (testResult.payloadSent) {
            <div class="mb-2">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <label class="form-label mb-0">Payload sent</label>
                <button type="button" class="btn btn-sm btn-soft-success" (click)="copyPayloadSent()">
                  <i class="ri-file-copy-line align-bottom me-1"></i>Copy
                </button>
              </div>
              <pre class="webhook-payload mb-0">{{ testResult.payloadSent }}</pre>
            </div>
          }

          @if (testResult.responseBody) {
            <div>
              <label class="form-label mb-1">Endpoint response</label>
              <pre class="webhook-payload mb-0">{{ testResult.responseBody }}</pre>
              @if (testResult.responseTruncated) {
                <div class="form-text">Response truncated for display.</div>
              }
            </div>
          }

        </div>
      }

      <div class="mt-3">
        <button type="button" class="btn btn-sm btn-link p-0" (click)="toggleShowLog()">
          <i class="align-bottom me-1" [class.ri-arrow-down-s-line]="!showLog" [class.ri-arrow-up-s-line]="showLog"></i>
          {{ showLog ? 'Hide' : 'Show' }} delivery log
        </button>

        @if (showLog && gotDgProperties) {
          <div class="mt-2">
            <app-grid-view #gridViewWebhookLog
              [apiPath]="dgApiPathWebhookLog"
              [dgActionColumn]="dgActionColumnWebhookLog"
              [dgColumns]="dgColumnsWebhookLog"
              [dgPopup]="dgPopupWebhookLog"
              [dgProperties]="dgPropertiesWebhookLog"
            ></app-grid-view>
          </div>
        }
      </div>
      }

    </div>

    <div class="modal-footer">
      <div class="col-12">
        <div class="row">
          <div class="col-12">
            <div class="hstack gap-2 justify-content-end">
              @if (activeTab==navItem_Webhook) {
                @if (canReplayLastCountryChange) {
                  <button
                    class="btn btn-soft-success"
                    [disabled]="testInProgress || hasUnsavedChanges"
                    [title]="hasUnsavedChanges ? 'Submit your changes before replaying.' : 'Re-fire the most recent live webhook event for this company. No customer SMS or email is re-sent.'"
                    type="button"
                    (click)="replayLastCountryChange()"
                  >
                    <i class="ri-refresh-line align-bottom me-1"></i>Replay Last Change
                  </button>
                }
                <button
                  class="btn btn-soft-success"
                  [disabled]="!canSendTest"
                  [title]="hasUnsavedChanges ? 'Submit your changes before testing.' : 'Send a test POST to the stored endpoint and show the response.'"
                  type="button"
                  (click)="sendTest()"
                >
                  <i class="ri-send-plane-line align-bottom me-1"></i>Send Test
                </button>
              }
              <button type="button" class="btn btn-light" data-bs-dismiss="modal" (click)="locationWebhookFormCancel()">Close</button>
              <button type="submit" class="btn btn-success" id="add-btn-location-webhook">Submit</button>
            </div>
          </div>
        </div>
      </div>
    </div>

  </form>

</ng-template>

<app-completed-modal-form #completedModalForm
  (modalClosed)="locationWebhookCompletedModalHasBeenClosed()"
></app-completed-modal-form>
